import React from 'react';
import styled from 'styled-components';

const SummaryContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const SummaryHeader = styled.div`
  background: #f8f9fa;
  padding: 1.25rem;
  border-bottom: 1px solid #e9ecef;
  
  h3 {
    margin: 0;
    color: #333;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const SummaryContent = styled.div`
  padding: 1.25rem;
`;

const OrderItems = styled.div`
  margin-bottom: 1.5rem;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  
  .ticket-name {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.25rem;
    font-size: 0.9rem;
  }
  
  .event-name {
    color: #666;
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }
  
  .quantity {
    color: #666;
    font-size: 0.8rem;
  }
  
  .seat-info {
    color: #667eea;
    font-size: 0.8rem;
    font-weight: 500;
  }
`;

const ItemPrice = styled.div`
  text-align: right;
  color: #333;
  font-weight: 600;
  font-size: 0.9rem;
`;

const PricingBreakdown = styled.div`
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  
  &.total {
    font-weight: 700;
    font-size: 1.1rem;
    color: #333;
    padding-top: 0.5rem;
    border-top: 1px solid #e9ecef;
    margin-top: 0.5rem;
    margin-bottom: 0;
  }
  
  &.savings {
    color: #10b981;
    font-weight: 600;
  }
  
  .label {
    color: #666;
    font-size: 0.9rem;
  }
  
  .amount {
    font-weight: 600;
    color: #333;
  }
`;

const SecurityBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #666;
  
  .icon {
    font-size: 0.9rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #666;
  padding: 2rem 1rem;
  
  .icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const CheckoutSummary = ({ order, cartItems, showDetails = false }) => {
    // Use order data if available, otherwise fall back to cart items
    const items = order?.items || cartItems;
    const hasItems = items && items.length > 0;

    if (!hasItems) {
        return (
            <SummaryContainer>
                <SummaryHeader>
                    <h3>
                        <span>📋</span>
                        Order Summary
                    </h3>
                </SummaryHeader>
                <EmptyState>
                    <div className="icon">🛒</div>
                    <p>No items in your order</p>
                </EmptyState>
            </SummaryContainer>
        );
    }

    const calculateSubtotal = () => {
        if (order?.subtotalAmount) return order.subtotalAmount;
        return items.reduce((total, item) => total + (item.totalPrice || item.unitPrice * item.quantity), 0);
    };

    const calculateFees = () => {
        if (order?.feesAmount) return order.feesAmount;
        const subtotal = calculateSubtotal();
        return subtotal * 0.1; // 10% service fee
    };

    const calculateTax = () => {
        if (order?.taxAmount) return order.taxAmount;
        return 0; // No tax for tickets in this example
    };

    const calculateTotal = () => {
        if (order?.totalAmount) return order.totalAmount;
        return calculateSubtotal() + calculateFees() + calculateTax();
    };

    const subtotal = calculateSubtotal();
    const fees = calculateFees();
    const tax = calculateTax();
    const total = calculateTotal();

    return (
        <SummaryContainer>
            <SummaryHeader>
                <h3>
                    <span>📋</span>
                    Order Summary
                </h3>
            </SummaryHeader>

            <SummaryContent>
                <OrderItems>
                    {items.map((item, index) => (
                        <OrderItem key={item.id || index}>
                            <ItemInfo>
                                <div className="ticket-name">
                                    {item.ticketTypeName || item.name}
                                </div>
                                <div className="event-name">
                                    {item.eventTitle || item.eventName}
                                </div>
                                <div className="quantity">
                                    Quantity: {item.quantity}
                                </div>
                                {item.seatAssignment === 'ASSIGNED' && (
                                    <div className="seat-info">
                                        Reserved Seating
                                    </div>
                                )}
                            </ItemInfo>
                            <ItemPrice>
                                ${(item.totalPrice || item.unitPrice * item.quantity).toFixed(2)}
                            </ItemPrice>
                        </OrderItem>
                    ))}
                </OrderItems>

                <PricingBreakdown>
                    <PriceRow>
                        <span className="label">Subtotal</span>
                        <span className="amount">${subtotal.toFixed(2)}</span>
                    </PriceRow>

                    {fees > 0 && (
                        <PriceRow>
                            <span className="label">Service Fee</span>
                            <span className="amount">${fees.toFixed(2)}</span>
                        </PriceRow>
                    )}

                    {tax > 0 && (
                        <PriceRow>
                            <span className="label">Tax</span>
                            <span className="amount">${tax.toFixed(2)}</span>
                        </PriceRow>
                    )}

                    <PriceRow className="total">
                        <span className="label">Total</span>
                        <span className="amount">${total.toFixed(2)}</span>
                    </PriceRow>
                </PricingBreakdown>

                {showDetails && (
                    <SecurityBadges>
                        <SecurityBadge>
                            <span className="icon">🔒</span>
                            <span>Secure Payment</span>
                        </SecurityBadge>
                        <SecurityBadge>
                            <span className="icon">✅</span>
                            <span>Instant Confirmation</span>
                        </SecurityBadge>
                        <SecurityBadge>
                            <span className="icon">📱</span>
                            <span>Mobile Tickets</span>
                        </SecurityBadge>
                    </SecurityBadges>
                )}
            </SummaryContent>
        </SummaryContainer>
    );
};

export default CheckoutSummary;
